import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { theme } from '../styles/theme';
import { Spinner } from '../styles/components';
import { useTitleDetails } from '../hooks/useTitleDetails';
import { formatCompactCurrency } from '../utils/formatters';
import type { BudgetApiClient } from '../api/client';
import { einzelplanName, lookupLabel, useT } from '../i18n';
import type { BudgetTableEntry } from '../types';

function formatSollSource(sollSource: string | null, year: number): string | null {
  if (!sollSource) return null;
  if (sollSource === 'BHH') return `BHH ${year}`;
  if (sollSource.startsWith('NHH')) return `${sollSource} ${year}`;
  if (sollSource === 'Rechnung') return `HHR ${year}`;
  return `${sollSource} ${year}`;
}

interface TitleDetailsPopoverProps {
  entry: BudgetTableEntry;
  apiClient: BudgetApiClient;
  budgetType: 'E' | 'A';
  /**
   * Years that actually have actuals (from /budget-totals, total_ist > 0). A
   * plan year stores ist = 0 rather than NULL, so a null check alone would
   * print "0 €" and cite a Haushaltsrechnung that doesn't exist yet.
   */
  yearsWithIst: Set<number>;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement | null>;
}

const PopoverContainer = styled.div<{ isVisible: boolean; top: number; left: number }>`
  position: fixed;
  top: ${props => props.top}px;
  left: ${props => props.left}px;
  min-width: 320px;
  max-width: 400px;
  max-height: calc(100vh - ${props => props.top}px - 16px);
  display: flex;
  flex-direction: column;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 12px 32px rgba(24, 28, 68, 0.18), 0 4px 8px rgba(0, 0, 0, 0.06);
  z-index: 10000;
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  transform: ${props => props.isVisible ? 'translateX(0)' : 'translateX(8px)'};
  transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
`;

const PopoverHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.border};
  background: ${theme.colors.background};
  flex-shrink: 0;
`;

// Popover header — sans-m (1rem) semibold.
const PopoverTitle = styled.h4`
  font-family: var(--sans-stack);
  font-size: 1rem;
  font-weight: ${theme.fontWeight.semibold};
  color: var(--color-text-default);
  margin: 0;
  flex: 1;
  min-width: 0;
  padding-right: ${theme.spacing.sm};
  line-height: 1.3;
  word-break: break-word;
`;

const CloseButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  font-size: 16px;
  border: none;
  background: transparent;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  transition: all 0.15s ease;

  &:hover {
    background: rgba(24, 28, 68, 0.08);
    color: ${theme.colors.text.primary};
  }
`;

const PopoverContent = styled.div`
  padding: ${theme.spacing.md};
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const InfoSection = styled.div`
  margin-bottom: ${theme.spacing.md};
`;

const InfoRow = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.xs};
`;

const InfoLabel = styled.span`
  font-family: var(--sans-stack);
  font-size: 1rem;
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.colors.text.secondary};
  min-width: 70px;
`;

const InfoValue = styled.span`
  font-family: var(--sans-stack);
  font-size: 1rem;
  color: ${theme.colors.text.primary};
  flex: 1;
`;

const TableSection = styled.div`
  border-top: 1px solid ${theme.colors.border};
  padding-top: ${theme.spacing.md};
`;

// Sub-section label inside the popover — matches the SectionTitle pattern
// used elsewhere in budget (small uppercase sans muted).
const TableTitle = styled.h5`
  font-family: var(--sans-stack);
  font-size: 1rem;
  font-weight: ${theme.fontWeight.semibold};
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: var(--sans-stack);
  font-size: 1rem;
`;

const TableHead = styled.thead`
  background: ${theme.colors.background};
`;

const Th = styled.th`
  text-align: left;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.colors.text.secondary};
  border-bottom: 1px solid ${theme.colors.border};

  &:nth-of-type(2),
  &:nth-of-type(3) {
    text-align: right;
  }
`;

const Td = styled.td`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  color: ${theme.colors.text.primary};
  border-bottom: 1px solid ${theme.colors.border};

  &:nth-of-type(2),
  &:nth-of-type(3) {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`;

const TableBody = styled.tbody`
  max-height: 200px;
  overflow-y: auto;
`;

const SourceText = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: 0.9em;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing.lg};
`;

const ErrorMessage = styled.div`
  padding: ${theme.spacing.md};
  color: ${theme.colors.error};
  font-family: var(--sans-stack);
  font-size: 1rem;
  text-align: center;
`;

const CloseIcon = () => (
  <span className="icon" data-icon="close" />
);

export function TitleDetailsPopover({
  entry,
  apiClient,
  budgetType,
  onClose,
  anchorRef,
  yearsWithIst,
}: TitleDetailsPopoverProps) {
  const m = useT();
  const containerRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, error } = useTitleDetails(apiClient, entry.id, budgetType);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isPositioned, setIsPositioned] = useState(false);

  // Calculate position based on anchor element
  useEffect(() => {
    const updatePosition = () => {
      if (!anchorRef.current) return;

      const anchorRect = anchorRef.current.getBoundingClientRect();
      const popoverWidth = 320; // min-width
      const popoverHeight = 350; // approximate height
      const margin = 8;

      // Position to the left of the button
      let left = anchorRect.left - popoverWidth - margin;
      let top = anchorRect.top + anchorRect.height / 2 - popoverHeight / 2;

      // Ensure popover doesn't go off-screen (top)
      if (top < margin) {
        top = margin;
      }

      // Ensure popover doesn't go off-screen (bottom)
      if (top + popoverHeight > window.innerHeight - margin) {
        top = window.innerHeight - popoverHeight - margin;
      }

      // If no room on left, position to the right
      if (left < margin) {
        left = anchorRect.right + margin;
      }

      // On narrow viewports neither side has room (the button sits at the
      // right edge of the horizontally scrolled table) — clamp into view.
      if (left + popoverWidth > window.innerWidth - margin) {
        left = window.innerWidth - popoverWidth - margin;
      }
      if (left < margin) {
        left = margin;
      }

      setPosition({ top, left });
      setIsPositioned(true);
    };

    updatePosition();
  }, [anchorRef]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, anchorRef]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Prevent page scroll when scrolling inside popup
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (event: WheelEvent) => {
      const content = container.querySelector('[data-scroll-content]') as HTMLElement;
      if (!content) return;

      const { scrollTop, scrollHeight, clientHeight } = content;
      const atTop = scrollTop === 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight;

      // Prevent scroll propagation at boundaries
      if ((event.deltaY < 0 && atTop) || (event.deltaY > 0 && atBottom)) {
        event.preventDefault();
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  const popoverContent = (
    <PopoverContainer ref={containerRef} isVisible={isPositioned} top={position.top} left={position.left}>
      <PopoverHeader>
        <PopoverTitle>{data?.zweckbestimmung ?? m.titleDetails.fallbackTitle}</PopoverTitle>
        <CloseButton onClick={onClose} title={m.common.close}>
          <CloseIcon />
        </CloseButton>
      </PopoverHeader>
      <PopoverContent data-scroll-content>
        {isLoading ? (
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : data ? (
          <>
            <InfoSection>
              <InfoRow>
                <InfoLabel>{m.titleDetails.einzelplan}</InfoLabel>
                <InfoValue>{data.einzelplan} - {einzelplanName(data.einzelplanText)}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>{m.titleDetails.kapitel}</InfoLabel>
                <InfoValue>{data.kapitel} - {lookupLabel('kapitel', data.kapitelText)}</InfoValue>
              </InfoRow>
            </InfoSection>
            <TableSection>
              <TableTitle>{m.titleDetails.history}</TableTitle>
              <DataTable>
                <TableHead>
                  <tr>
                    <Th>{m.titleDetails.year}</Th>
                    <Th>{m.common.soll}</Th>
                    <Th>{m.common.ist}</Th>
                  </tr>
                </TableHead>
                <TableBody>
                  {data.yearlyData.map((row) => (
                    <tr key={row.year}>
                      <Td>{row.year}</Td>
                      <Td>
                        {formatCompactCurrency(row.soll)}
                        {row.soll !== null && (
                          <SourceText>({formatSollSource(row.sollSource, row.year) ?? `BHH ${row.year}`}, {m.common.page(row.pageSoll)})</SourceText>
                        )}
                      </Td>
                      <Td>
                        {row.ist !== null && yearsWithIst.has(row.year) ? (
                          <>
                            {formatCompactCurrency(row.ist)}
                            <SourceText>(HHR {row.year}, {m.common.page(row.pageIst)})</SourceText>
                          </>
                        ) : '-'}
                      </Td>
                    </tr>
                  ))}
                </TableBody>
              </DataTable>
            </TableSection>
          </>
        ) : null}
      </PopoverContent>
    </PopoverContainer>
  );

  return createPortal(popoverContent, document.body);
}
